import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  type DocumentData,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { useCallback, useEffect, useRef, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { publicEventSchema, type PublicEvent } from "./schemas";

export type PublishedEventsState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; events: PublicEvent[]; hasMore: boolean };

/**
 * One page of the browse surface.
 *
 * `public_events` grows by one document per published event and only event
 * deletion removes one, so reading the whole collection is an unbounded number
 * of billed reads on every visit to the page. The page is therefore a cursor
 * walk: PAGE_SIZE + 1 documents a request, the extra one answering "is there
 * another page" without a count query.
 *
 * KEEP PAGE_SIZE + 1 AT OR BELOW THE CAP IN firestore.rules. The `list` rule on
 * `public_events` refuses a request whose limit exceeds 50, and it refuses a
 * query that names no limit at all. Raising this past that cap turns the whole
 * page into a permission error that names no field.
 * use-published-events.test.ts reads the cap out of the rules file and fails
 * instead.
 */
const PAGE_SIZE = 24;

/**
 * Ordering by `startDate` is safe on this collection, though it would not be on
 * `events`.
 *
 * Firestore's `orderBy` drops a document that has no value for the field, which
 * is why use-governance-events sorts client-side over `events`, where an
 * imported or half-written event can be missing dates. The projection behind
 * `public_events` writes `startDate` on every document unconditionally and
 * PublicEventProjectionPlan rejects a document whose key set differs from the
 * contract, so no document here can be dropped by the sort.
 *
 * Descending puts the furthest-future event first and sinks past events, and
 * it is a single-field sort, which Firestore indexes automatically. No index is
 * declared for it in infrastructure/gcp/terraform/firestore.tf and none is
 * needed.
 */
const ORDER_FIELD = "startDate";

function parseEvents(
  documents: QueryDocumentSnapshot<DocumentData>[],
): PublicEvent[] {
  return documents.map((document) => {
    const result = publicEventSchema.safeParse(document.data());
    if (!result.success) {
      // Loud, not silent. Dropping the document would hide one event; if the
      // projection's field contract and this schema ever drift, dropping would
      // hide every event behind an empty page that looks like "no events yet".
      throw new Error(`Public event ${document.id} could not be read`);
    }
    return result.data;
  });
}

/**
 * Archived events are not discovery: their link keeps working (the projection
 * document stays), but the catalogue does not offer them. Filtered here and
 * not in the query, because `where("archived", "!=", true)` would drop every
 * document written before the field existed and a new composite index would
 * need the backend's agreement. The cost is that a page can hold fewer than
 * PAGE_SIZE events, or none, while `hasMore` is still true: it answers "is
 * there another page of documents", counted before the filter.
 */
function visibleEvents(events: PublicEvent[]): PublicEvent[] {
  return events.filter((event) => event.archived !== true);
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : "Failed to load events";
}

export function usePublishedEvents(): PublishedEventsState & {
  loadMore: () => Promise<void>;
  loadingMore: boolean;
  /** A failed "show more", which leaves the pages already read on screen. */
  loadMoreError: string | null;
} {
  const [state, setState] = useState<PublishedEventsState>({
    status: "loading",
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const lastDocument = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  // A ref, because two calls in one tick both read the same `loadingMore`
  // state and would issue the same cursor query twice, appending the page
  // twice. The button's `disabled` hides that today; an infinite scroll would
  // not.
  const inFlight = useRef(false);

  useEffect(() => {
    let cancelled = false;

    getDocs(
      query(
        collection(db, "public_events"),
        orderBy(ORDER_FIELD, "desc"),
        limit(PAGE_SIZE + 1),
      ),
    )
      .then((snapshot) => {
        if (cancelled) return;
        const page = snapshot.docs.slice(0, PAGE_SIZE);
        lastDocument.current = page.at(-1) ?? null;
        setState({
          status: "success",
          events: visibleEvents(parseEvents(page)),
          hasMore: snapshot.docs.length > PAGE_SIZE,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setState({ status: "error", error: message(error) });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const loadMore = useCallback(async () => {
    if (
      state.status !== "success" ||
      !state.hasMore ||
      !lastDocument.current ||
      inFlight.current
    ) {
      return;
    }
    inFlight.current = true;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const snapshot = await getDocs(
        query(
          collection(db, "public_events"),
          orderBy(ORDER_FIELD, "desc"),
          // The document snapshot, not its startDate. Two events may share a
          // start date, and a value cursor would then repeat or skip the ones
          // that tie across the page boundary.
          startAfter(lastDocument.current),
          limit(PAGE_SIZE + 1),
        ),
      );
      const page = snapshot.docs.slice(0, PAGE_SIZE);
      const events = visibleEvents(parseEvents(page));
      lastDocument.current = page.at(-1) ?? lastDocument.current;
      setState((current) =>
        current.status === "success"
          ? {
              status: "success",
              events: [...current.events, ...events],
              hasMore: snapshot.docs.length > PAGE_SIZE,
            }
          : current,
      );
    } catch (error) {
      // The pages already read stay on screen. Replacing them with an error
      // would blank a catalogue somebody was reading over one failed request,
      // and `loadMore` only runs from the success state, so there would be no
      // way back without a remount.
      setLoadMoreError(message(error));
    } finally {
      inFlight.current = false;
      setLoadingMore(false);
    }
  }, [state]);

  return { ...state, loadMore, loadingMore, loadMoreError };
}

export const PUBLISHED_EVENTS_PAGE_SIZE = PAGE_SIZE;
